import { H1 } from '@/common/ui'
import useUserStore from '@/user/userStore'
import useAccountStore from '@/account/accountStore'
import { usePrimaryAccount } from '@/account/usePrimaryAccount'
import { useCurrentAccount } from '@/account/useCurrentAccount'
import { PortfolioSummary } from './PortfolioSummary'
import { PortfolioValue } from './PortfolioValue'
import { GettingStartedPreview } from './GettingStartedPreview'
import { UserCampaignsSummary } from './UserCampaignsSummary'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { OnboardingAlerts } from './OnboardingAlerts'
import { useFetchKYC } from '@/kyc/kycQueries'
import { useFetchTradingAccount } from '@/trading/tradingQueries'
import { useKycCompletionState } from '@/kyc/hooks/useKycCompletionState'
import WalletSummary from './WalletSummary'
import BundleCards from '../invest/bundles'
import { usePwaContext } from '@/common/pwa/pwaContext'
import { useFeaturePromotionViewed } from '@/user/userQueries'
import { AccountType, AccountStatus, FeaturePromotion } from '@/common/types'
import { useFetchAccountDetails } from '@/wallet/walletQueries'
import { NotFoundError } from '@/common/api/error'
import MoreBlogs from '@/blogs/MoreBlogs'
import { DASHBOARD_COPY } from '@/locales/en'
import { TrendingEtfs } from '@/invest/trendingEtfs'
import { ORDER_FROM } from '@/common/context/orderSourceValues'
import { APP_ROUTES, KYC_STATUS } from '@/common/constants'

export function Dashboard() {
    const featurePromotionViewed = useFeaturePromotionViewed()
    const { user } = useUserStore()
    const account = useCurrentAccount()
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const primaryAccount = usePrimaryAccount()
    const { openInstallModal, isInstallModalOpen, available } = usePwaContext()
    const [installDismissed, setInstallDismissed] = useState(false)
    const wasOpenRef = useRef(false)

    const pollMs = 30000 // 30 seconds
    const { data: kyc, isLoading: isLoadingKyc } = useFetchKYC(activeAccountId, {
        refetchInterval: (data: any) => {
            if (!data) return false
            const interimStatuses = [KYC_STATUS.INITIATED, KYC_STATUS.RE_INITIATED, KYC_STATUS.UNDER_VERIFICATION]
            return interimStatuses.includes(data?.status ?? '') ? pollMs : false
        },
    })

    const { isInGracePeriod } = useKycCompletionState(kyc)

    const isBankAccountCheckEnabled = account?.accountStatus === AccountStatus.ACTIVE && !!activeAccountId
    const {
        data: bankAccountDetails,
        isLoading: isFetchingBankAccount,
        error: bankAccountError,
    } = useFetchAccountDetails(activeAccountId, isBankAccountCheckEnabled)
    const hasBankAccount = !isBankAccountCheckEnabled
        ? undefined
        : isFetchingBankAccount
          ? undefined
          : bankAccountError
            ? bankAccountError instanceof NotFoundError
                ? false
                : undefined
            : !!bankAccountDetails

    const { data: tradingAccount } = useFetchTradingAccount(activeAccountId, {
        refetchInterval: (data) => {
            if (kyc?.status !== KYC_STATUS.VERIFICATION_SUCCESS) return false
            if (!data) return pollMs
            return data?.status === 'APPROVED' ? false : pollMs
        },
    })
    const [hidePledged, setHidePledged] = useState<boolean>(() =>
        JSON.parse(localStorage.getItem('hidePledged') ?? 'false'),
    )

    useEffect(() => {
        if (user?.featurePromotionFlags?.['pwa-app-install'] == false || installDismissed) return

        if (wasOpenRef.current) return
        if (!available) return
        wasOpenRef.current = true
        openInstallModal?.()
    }, [user?.featurePromotionFlags, openInstallModal, installDismissed, available])

    useEffect(() => {
        if (user?.featurePromotionFlags?.['pwa-app-install'] == false || installDismissed) return

        if (isInstallModalOpen) {
            wasOpenRef.current = true
            return
        }

        if (!wasOpenRef.current) return

        setInstallDismissed(true)
        featurePromotionViewed.mutate(FeaturePromotion.PWA_APP_INSTALL)
    }, [isInstallModalOpen, user?.featurePromotionFlags, featurePromotionViewed, installDismissed])

    const welcomeText = parseInt(moment().format('HH')) < 12 ? 'Good morning' : 'Good afternoon'
    const effectiveAccountType = account?.accountType ?? user?.accountType
    const isAdviserAccount = primaryAccount?.accountType === AccountType.ADVISER
    return (
        <main>
            <H1 className="text-[32px] mb-6">
                {isAdviserAccount ? `${account?.accountName ?? ''}` : `${welcomeText} ${user?.firstName} 🌱`}
            </H1>

            <OnboardingAlerts
                kyc={kyc}
                isLoadingKyc={isLoadingKyc}
                accountType={effectiveAccountType}
                tradingAccountStatus={tradingAccount?.status}
                hasBankAccount={hasBankAccount}
                activeAccountId={activeAccountId}
                showMobileResumeApplication={
                    !isInGracePeriod &&
                    (Boolean(kyc?.canInitiateKyc) || !kyc) &&
                    effectiveAccountType !== AccountType.ADVISER
                }
                resumeApplicationTo={APP_ROUTES.APPLICATION_RESUME}
            />
            <div className="mb-10 grid grid-cols-1 gap-6">
                <PortfolioSummary
                    accountId={activeAccountId}
                    hidePledged={hidePledged}
                    setHidePledged={setHidePledged}
                />
                <WalletSummary />
                <PortfolioValue />
                <BundleCards />
            </div>
            <UserCampaignsSummary />
            <TrendingEtfs className="my-10" orderFrom={ORDER_FROM.DASHBOARD} />
            <GettingStartedPreview />
            <div className="mt-10 grid grid-cols-1 gap-6">
                <MoreBlogs title={DASHBOARD_COPY.blogs} />
            </div>
        </main>
    )
}
